import { useEffect, useState } from 'react';
import type { User } from 'firebase/auth';
import { getAuthInstance } from './firebase';

export const PAUL_AUTHORIZED_EMAIL = 'paul@appsite.ca';

export type PaulAccessSnapshot = {
  email: string | null;
  isPaul: boolean;
  isReady: boolean;
};

const DEFAULT_SNAPSHOT: PaulAccessSnapshot = {
  email: null,
  isPaul: false,
  isReady: false,
};

let snapshot: PaulAccessSnapshot = DEFAULT_SNAPSHOT;
let initialized = false;
let initPromise: Promise<void> | null = null;
const listeners = new Set<(nextSnapshot: PaulAccessSnapshot) => void>();

const normalizeEmail = (email?: string | null): string | null => {
  if (typeof email !== 'string') return null;
  const normalized = email.trim().toLowerCase();
  return normalized.length > 0 ? normalized : null;
};

// Email persisted at sign-in (auth.ts). Anyone can write sessionStorage from the console, so it
// is NEVER trusted to grant access — it only tells us a signed-in session probably exists, so a
// transient null from onAuthStateChanged during session restore waits (isReady: false) for up
// to RESTORE_GRACE_MS instead of flashing "Restricted". Access is decided from the live user.
const RESTORE_GRACE_MS = 5000;

const readBackupEmail = (): string | null => {
  try {
    if (typeof sessionStorage === 'undefined') return null;
    return sessionStorage.getItem('firebase-auth-email-backup');
  } catch {
    return null;
  }
};

const emitSnapshot = () => {
  listeners.forEach((listener) => listener(snapshot));
};

const setSnapshot = (nextSnapshot: PaulAccessSnapshot) => {
  snapshot = nextSnapshot;
  emitSnapshot();
};

export const isPaulEmail = (email?: string | null): boolean =>
  normalizeEmail(email) === PAUL_AUTHORIZED_EMAIL;

export const isPaulAuthorizedUser = (user?: Pick<User, 'email'> | null): boolean =>
  isPaulEmail(user?.email ?? null);

const initializePaulAccess = async (): Promise<void> => {
  if (initialized) return;
  if (initPromise) return initPromise;

  initPromise = (async () => {
    try {
      const auth = await getAuthInstance();
      if (!auth) {
        initialized = true;
        setSnapshot({ email: null, isPaul: false, isReady: true });
        return;
      }

      const { onAuthStateChanged } = await import('firebase/auth');
      let graceTimer: ReturnType<typeof setTimeout> | null = null;
      // Auth events can overlap (getIdTokenResult is async); only the latest one may publish.
      let eventSeq = 0;

      onAuthStateChanged(auth, async (user) => {
        const seq = ++eventSeq;
        if (graceTimer) {
          clearTimeout(graceTimer);
          graceTimer = null;
        }

        if (!user) {
          if (readBackupEmail() && !snapshot.isReady) {
            // Probably a session-restore hiccup: keep "checking" and wait for the real user.
            graceTimer = setTimeout(() => {
              graceTimer = null;
              if (seq !== eventSeq) return;
              initialized = true;
              setSnapshot({ email: null, isPaul: false, isReady: true });
            }, RESTORE_GRACE_MS);
            return;
          }
          initialized = true;
          setSnapshot({ email: null, isPaul: false, isReady: true });
          return;
        }

        const liveEmail = normalizeEmail(user.email ?? null);
        // Users with the `admin` custom claim are execs too — the same rule Firestore
        // (isExec) and the Functions requireExec gate already apply server-side.
        let hasAdminClaim = false;
        try {
          const { claims } = await user.getIdTokenResult();
          hasAdminClaim = claims.admin === true;
        } catch {
          hasAdminClaim = false;
        }
        if (seq !== eventSeq) return;
        initialized = true;
        setSnapshot({
          email: liveEmail,
          isPaul: hasAdminClaim || isPaulEmail(liveEmail),
          isReady: true,
        });
      });
    } catch {
      initialized = true;
      setSnapshot({ email: null, isPaul: false, isReady: true });
    }
  })();

  return initPromise;
};

export const getPaulAccessSnapshot = (): PaulAccessSnapshot => {
  void initializePaulAccess();
  return snapshot;
};

export const isPaulAuthorizedCached = (): boolean => getPaulAccessSnapshot().isPaul;

export const subscribePaulAccess = (
  listener: (nextSnapshot: PaulAccessSnapshot) => void,
): (() => void) => {
  listeners.add(listener);
  listener(snapshot);
  void initializePaulAccess();

  return () => {
    listeners.delete(listener);
  };
};

export const usePaulAccess = (): PaulAccessSnapshot => {
  const [state, setState] = useState<PaulAccessSnapshot>(() => getPaulAccessSnapshot());

  useEffect(() => subscribePaulAccess(setState), []);

  return state;
};